import { FC, useCallback, useEffect, useRef, useState } from 'react';

import debounce from 'lodash/debounce';

import AutoComplete, { Option } from '@shared/AutoComplete';
import Drawer from '@components/Drawer';
import { callService } from '@api/caller';
import { AirportSearchViewModel, WebUrlType } from '@api/index';
import { LocationIcon, PlaneIcon } from '@assets/icons';
import { FlexibleDiv } from '@components/formStyles';
import {
  ItemText,
  ItemTextCity,
  ItemTextWrapper,
} from '@components/ui/search/SearchResultListStyle';
import CustomIcon, { SeturIcon } from '@components/utils/CustomIcon';
import makeBold from '@utils/makeBold';
import ResponsiveApplyButton from '@components/views/components/listing/drawer/ResponsiveApplyButton/ResponsiveApplyButton';
import { PastSearches, EmptyLocationSelector } from '@shared';
import { useOutsideClick } from '@hooks/hooks';
import { POPULAR_FLIGHTS } from '@components/views/flight/constants';
import { useBreakpoint } from '@contexts/breakpoints';

import {
  Arrow,
  ErrorMessage,
  ErrorMessageContainer,
  InputWrapper,
  ItemErrorText,
  ModalWrapper,
  OptionWrapper,
  SelectWrapper,
} from './styled';

export type AirPortTypes = AirportSearchViewModel | null;
type PropsTypes = {
  label: string;
  onChange?: (airport: AirPortTypes) => void;
  icon?: SeturIcon;
  isStrokeIcon?: boolean;
  value?: AirPortTypes;
  history?: AirportSearchViewModel[];
  clearPastFlightSearch?: () => void;
  removePastFlightSearch?: (item: AirportSearchViewModel) => void;
  isLocationError: boolean;
  setIsLocationError: (value: boolean) => void;
  activeLocation?: AirPortTypes;
};

const AirportAutoComplate: FC<PropsTypes> = ({
  label,
  onChange: pOnChange = (v) => v,
  icon,
  isStrokeIcon = false,
  value,
  history,
  removePastFlightSearch,
  clearPastFlightSearch,
  isLocationError,
  setIsLocationError,
  activeLocation,
}) => {
  const searchWrapperRef = useRef<HTMLDivElement>(null);

  const { xl: isDesktop, xs: isMobile } = useBreakpoint();

  const [data, setData] = useState<Array<AirportSearchViewModel> | []>([]);
  const [isFocus, setIsFocus] = useState(false);
  const [isAirportResultsLoading, setIsAirportResultsLoading] = useState(false);
  const [isDataFetched, setIsDataFetched] = useState(false);
  const [isHeaderOpen, setIsHeaderOpen] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState('');
  const [isShowEmptyLocationSelector, setIsShowEmptyLocationSelector] =
    useState(false);

  const searchAirport = useCallback(
    debounce((s: string) => fetchData(s), 500),
    []
  );

  useEffect(() => {
    if (value) {
      setInputValue(
        `${value?.airportName || ''}${
          value?.airportCode ? ', ' + value?.airportCode : ''
        }`
      );
    }
  }, [value]);

  useEffect(() => {
    if (inputValue) {
      const isDifferentValue =
        `${activeLocation?.airportName || ''}${
          activeLocation?.airportCode ? ', ' + activeLocation?.airportCode : ''
        }` !== inputValue;
      if (inputValue.length > 2 && isDifferentValue) searchAirport(inputValue);
    } else {
      setData([]);
    }
  }, [inputValue]);

  useOutsideClick(searchWrapperRef, () => {
    setIsShowEmptyLocationSelector(false);
  });

  const handleSearch = (value: string) => {
    setInputValue(value);
    if (value === '') {
      pOnChange(null);
    }
    setIsDataFetched(false);
  };

  const handleFocus = () => {
    setIsFocus(true);
    isLocationError && setIsLocationError(false);
    setIsShowEmptyLocationSelector(true);
  };

  const handleBlur = () => {
    setIsFocus(false);
    isLocationError && setIsLocationError(false);
  };

  const fetchData = async (s: string) => {
    try {
      setIsAirportResultsLoading(true);
      const airports = await callService('SearchesService', 'searchesFlight', {
        s: s,
      });
      setData(airports);
    } catch (error) {
      setData([]);
    } finally {
      setIsAirportResultsLoading(false);
      setIsDataFetched(true);
    }
  };
  const handleHeaderOpen = () => setIsHeaderOpen((oldState) => !oldState);

  const selectValue = (option: AirPortTypes) => {
    pOnChange(option);
    setInputValue(
      `${option?.airportName || ''}${
        option?.airportCode ? ', ' + option?.airportCode : ''
      }`
    );
  };

  const isShowItemTextError =
    isDataFetched &&
    !isAirportResultsLoading &&
    inputValue.length > 2 &&
    data &&
    Array.isArray(data) &&
    !data.length;

  const showPastSearch =
    ((Array.isArray(history) && history?.length > 0) ||
      (Array.isArray(POPULAR_FLIGHTS) && POPULAR_FLIGHTS?.length > 0)) &&
    inputValue.length === 0
      ? true
      : false;

  return (
    <>
      {!isDesktop ? (
        <>
          <SelectWrapper>
            <InputWrapper onClick={handleHeaderOpen}>
              <AutoComplete
                onSearch={handleSearch}
                onFocus={handleFocus}
                onBlur={handleBlur}
                allowClear
                isError={isLocationError}
                value={inputValue}
                dropdownStyle={{
                  minWidth: isMobile ? 'auto' : '400px',
                }}
                icon={icon}
                isStrokeIcon={isStrokeIcon}
                label={label}
              />
            </InputWrapper>
            <Drawer
              title={label}
              visible={isHeaderOpen}
              onClose={handleHeaderOpen}
              placement='top'
              footer={
                <ResponsiveApplyButton
                  title='Uygula'
                  onClick={handleHeaderOpen}
                />
              }
            >
              <SelectWrapper isHeaderOpen={isHeaderOpen}>
                <AutoComplete
                  onSearch={handleSearch}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  allowClear
                  isError={isLocationError}
                  value={inputValue}
                  dropdownStyle={{
                    minWidth: isMobile ? 'auto' : '400px',
                  }}
                  icon={icon}
                  isStrokeIcon={isStrokeIcon}
                  label={label}
                >
                  <>
                    {showPastSearch ? (
                      <>
                        <Option className='pastSearchesTitleWrapper'>
                          <PastSearches.Header
                            label='Son Aramalar'
                            handleClear={() => clearPastFlightSearch?.()}
                          />
                        </Option>
                        {history?.map((item) => (
                          <Option key={item?.airportCode}>
                            <PastSearches.Item
                              label={
                                item?.airportName +
                                item?.countryName +
                                item?.cityName
                              }
                              onClick={() => selectValue(item)}
                              type={
                                item?.isCity
                                  ? WebUrlType.FLIGHT_CITY_LANDING
                                  : WebUrlType.FLIGHT_LANDING
                              }
                              handleRemove={() =>
                                removePastFlightSearch?.(item)
                              }
                            />
                          </Option>
                        ))}
                      </>
                    ) : null}
                    {data && Array.isArray(data) && data.length ? (
                      data?.map((option) => (
                        <Option
                          key={`${option.airportId}-${option.airportCode}`}
                        >
                          <OptionWrapper onClick={() => selectValue(option)}>
                            <CustomIcon
                              fontSize={24}
                              paletteColor='grey40'
                              icon={option.isCity ? LocationIcon : PlaneIcon}
                            />
                            <FlexibleDiv right={17} />
                            <ItemTextWrapper>
                              {option.isCity ? (
                                <>
                                  <ItemText
                                    dangerouslySetInnerHTML={{
                                      __html: makeBold(
                                        `${option.cityName},${option.countryName}`,
                                        inputValue
                                      ),
                                    }}
                                  />
                                  <ItemTextCity
                                    dangerouslySetInnerHTML={{
                                      __html: makeBold(
                                        `${option.airportName}`,
                                        inputValue
                                      ),
                                    }}
                                  />
                                </>
                              ) : (
                                <>
                                  <ItemText
                                    dangerouslySetInnerHTML={{
                                      __html: makeBold(
                                        `${option.airportName},${option.airportCode}`,
                                        inputValue
                                      ),
                                    }}
                                  />
                                  <ItemTextCity
                                    dangerouslySetInnerHTML={{
                                      __html: makeBold(
                                        `${option.cityName},${option.countryName}`,
                                        inputValue
                                      ),
                                    }}
                                  />
                                </>
                              )}
                            </ItemTextWrapper>
                          </OptionWrapper>
                        </Option>
                      ))
                    ) : isShowItemTextError ? (
                      <Option>
                        <OptionWrapper>
                          <ItemErrorText>
                            {!isMobile ? (
                              <span>
                                Aradığınız kriterlere uygun sonuç bulunamadı.
                              </span>
                            ) : (
                              <span>
                                Aradığınız kriterlere uygun <br />
                                sonuç bulunamadı.
                              </span>
                            )}
                          </ItemErrorText>
                        </OptionWrapper>
                      </Option>
                    ) : null}
                  </>
                </AutoComplete>
              </SelectWrapper>
              <ModalWrapper />
            </Drawer>

            {isLocationError && (
              <ErrorMessageContainer>
                <Arrow />
                <ErrorMessage>Şehir Adını Kontrol Ediniz</ErrorMessage>
              </ErrorMessageContainer>
            )}
          </SelectWrapper>
        </>
      ) : (
        <SelectWrapper ref={searchWrapperRef}>
          <AutoComplete
            onSearch={handleSearch}
            onFocus={handleFocus}
            onBlur={handleBlur}
            allowClear
            isFocus={isFocus}
            isError={isLocationError}
            value={inputValue}
            icon={icon}
            isStrokeIcon={isStrokeIcon}
            label={label}
          >
            <>
              {data && Array.isArray(data) && data.length ? (
                data?.map((option) => (
                  <Option key={`${option.airportId}-${option.airportCode}`}>
                    <OptionWrapper onClick={() => selectValue(option)}>
                      <CustomIcon
                        fontSize={24}
                        {...(option.isCity
                          ? {
                              paletteColor: 'grey800',
                              icon: LocationIcon,
                            }
                          : {
                              stroke: 'grey800',
                              icon: PlaneIcon,
                            })}
                      />
                      <FlexibleDiv right={17} />
                      <ItemTextWrapper>
                        {option.isCity ? (
                          <>
                            <ItemText
                              dangerouslySetInnerHTML={{
                                __html: makeBold(
                                  `${option.cityName},${option.countryName}`,
                                  inputValue
                                ),
                              }}
                            />
                            <ItemTextCity
                              dangerouslySetInnerHTML={{
                                __html: makeBold(
                                  `${option.airportName}`,
                                  inputValue
                                ),
                              }}
                            />
                          </>
                        ) : (
                          <>
                            <ItemText
                              dangerouslySetInnerHTML={{
                                __html: makeBold(
                                  `${option.airportName},${option.airportCode}`,
                                  inputValue
                                ),
                              }}
                            />
                            <ItemTextCity
                              dangerouslySetInnerHTML={{
                                __html: makeBold(
                                  `${option.cityName},${option.countryName}`,
                                  inputValue
                                ),
                              }}
                            />
                          </>
                        )}
                      </ItemTextWrapper>
                    </OptionWrapper>
                  </Option>
                ))
              ) : isShowItemTextError ? (
                <Option>
                  <OptionWrapper>
                    <ItemErrorText>
                      {!isMobile ? (
                        <span>
                          Aradığınız kriterlere uygun sonuç bulunamadı.
                        </span>
                      ) : (
                        <span>
                          Aradığınız kriterlere uygun <br />
                          sonuç bulunamadı.
                        </span>
                      )}
                    </ItemErrorText>
                  </OptionWrapper>
                </Option>
              ) : null}
            </>
          </AutoComplete>
          {showPastSearch && isShowEmptyLocationSelector ? (
            <EmptyLocationSelector.Wrapper>
              {history?.length ? (
                <EmptyLocationSelector.ColumnWrapper>
                  <EmptyLocationSelector.ColumnHeader>
                    <PastSearches.Header
                      label='Son Aramalar'
                      handleClear={() => clearPastFlightSearch?.()}
                    />
                  </EmptyLocationSelector.ColumnHeader>
                  {history?.slice(0, 6)?.map((item) => (
                    <EmptyLocationSelector.LocationItem
                      key={item?.airportCode}
                      onClick={() => {
                        selectValue(item);
                        setIsShowEmptyLocationSelector(false);
                      }}
                    >
                      <PastSearches.Item
                        label={
                          item?.cityName +
                          ',' +
                          item?.countryName +
                          '(' +
                          item?.airportCode +
                          ')'
                        }
                        subLabel={item?.airportName}
                        type={
                          item?.isCity
                            ? WebUrlType.FLIGHT_CITY_LANDING
                            : WebUrlType.FLIGHT_LANDING
                        }
                        handleRemove={() => removePastFlightSearch?.(item)}
                      />
                    </EmptyLocationSelector.LocationItem>
                  ))}
                </EmptyLocationSelector.ColumnWrapper>
              ) : null}
              {POPULAR_FLIGHTS?.length ? (
                <EmptyLocationSelector.ColumnWrapper>
                  <EmptyLocationSelector.ColumnHeader>
                    <PastSearches.Header
                      label='Popüler Aramalar'
                      iconType='popular'
                    />
                  </EmptyLocationSelector.ColumnHeader>
                  {POPULAR_FLIGHTS?.map((item) => (
                    <EmptyLocationSelector.LocationItem
                      key={item?.airportCode}
                      onClick={() => {
                        selectValue(item);
                        setIsShowEmptyLocationSelector(false);
                      }}
                    >
                      <PastSearches.Item
                        label={
                          item?.cityName +
                          ',' +
                          item?.countryName +
                          '(' +
                          item?.airportCode +
                          ')'
                        }
                        subLabel={item?.airportName}
                        type={
                          item?.isCity
                            ? WebUrlType.FLIGHT_CITY_LANDING
                            : WebUrlType.FLIGHT_LANDING
                        }
                      />
                    </EmptyLocationSelector.LocationItem>
                  ))}
                </EmptyLocationSelector.ColumnWrapper>
              ) : null}
            </EmptyLocationSelector.Wrapper>
          ) : null}
          {isLocationError && (
            <ErrorMessageContainer>
              <Arrow />
              <ErrorMessage>Şehir Adını Kontrol Ediniz</ErrorMessage>
            </ErrorMessageContainer>
          )}
        </SelectWrapper>
      )}
    </>
  );
};

export default AirportAutoComplate;
